import { useMutation } from "@apollo/client/react";
import {
  SendSimulationCaseCompletionMessage,
  SendSimulationCaseCompletionMessageVariables,
  SimulationCaseById,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Section } from "common/components/Section";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import { GET_CASE_COMPLETION, QUERY } from "domain/common/simulation-mutations";
import { simulationBreadcrumb } from "domain/pages/breadcrumbs";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type RouteProps = {
  simulationCaseId: string;
};

interface Props {
  cancel(): void;
  complete(): void;
}

interface FormValues {
  prompt: string;
}

export const AutoGenerateSimulation: React.FC<
  React.PropsWithChildren<Props>
> = ({ cancel, complete }) => {
  const { simulationCaseId } = useParams<RouteProps>();
  const navigate = useNavigate();
  const previousPath = usePreviousPath();
  const methods = useForm<FormValues>();

  const { data, loadingOrError, refetch } = useSimpleQuery<SimulationCaseById>(
    QUERY,
    {
      variables: { id: simulationCaseId },
    },
  );

  const [mutate, { error, loading }] = useMutation<
    SendSimulationCaseCompletionMessage,
    SendSimulationCaseCompletionMessageVariables
  >(GET_CASE_COMPLETION);

  const onSubmit = async (input: FormValues) => {
    const newMessage = { content: input.prompt, role: "user" };
    const dataMessage = { content: data, role: "user" };

    const result = await mutate({
      variables: {
        input: {
          content: [newMessage, dataMessage],
          id: simulationCaseId,
        },
      },
    });

    if (result.data) {
      exitEditPage();
    }
  };

  if (!simulationCaseId) {
    throw new Error("Unexpected error: Missing simulationCaseId");
  }

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.simulationCaseById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const exitEditPage = () => {
    if (previousPath && !previousPath.includes("/admin")) {
      navigate(-1);
    } else {
      navigate(urls.simulationAdminOverview);
    }
  };

  return (
    <AdministrationLayout
      title={data.simulationCaseById.title ?? "Simulering"}
      breadcrumbItems={simulationBreadcrumb}
    >
      <Section title="Instruksjoner for KI-generert case">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <TextField
              title="Instruksjoner"
              fieldProps={methods.register("prompt", {
                required: "Du må gi casen en instruksjon",
              })}
              placeholder="Instruksjoner"
            />

            <MutationLoadingOrError loading={loading} error={error} />

            <CancelOrSubmit
              onCancel={exitEditPage}
              isLoading={loading}
              submitText="Generer case"
            />
          </form>
        </FormProvider>
      </Section>
    </AdministrationLayout>
  );
};
